import type { MessengerMessage } from '@/api/client';
import { isMessengerImageMime } from '@/components/Messenger/shared/attachments/messengerImageMime';

/** Метаданные треда в событии сокета (для заголовка группы и навигации). */
export type MessengerSocketThreadMeta = {
  thread_kind: 'direct' | 'group';
  title: string | null;
  peer_id?: string | null;
  peer_name?: string | null;
};

type ContextItem = { type: 'project' | 'session'; id: string; name?: string; date?: string; label?: string };

function normalizeContextAttachments(raw: unknown): ContextItem[] {
  if (!Array.isArray(raw)) return [];
  const out: ContextItem[] = [];
  for (const a of raw) {
    if (!a || typeof a !== 'object') continue;
    const o = a as Record<string, unknown>;
    if (o.type === 'project' && typeof o.id === 'string' && o.id.trim()) {
      out.push({
        type: 'project',
        id: o.id.trim(),
        name: typeof o.name === 'string' ? o.name : undefined,
      });
    } else if (
      o.type === 'session' &&
      typeof o.id === 'string' &&
      o.id.trim() &&
      typeof o.date === 'string' &&
      o.date.trim()
    ) {
      out.push({
        type: 'session',
        id: o.id.trim(),
        date: o.date.trim(),
        label: typeof o.label === 'string' ? o.label : undefined,
      });
    }
  }
  return out;
}

function formatContextLine(items: ContextItem[]): string {
  if (items.length === 0) return '';
  const parts = items.map((it) => {
    if (it.type === 'project') {
      const n = it.name?.trim() || it.id;
      return `Проект: ${n}`;
    }
    const lbl = it.label?.trim() || it.date;
    return `Сессия: ${lbl}`;
  });
  const first = parts[0]!;
  if (parts.length === 1) return first;
  return `${first} ещё ${parts.length - 1}`;
}

function senderShortName(msg: MessengerMessage): string {
  return (msg.sender_display_name ?? '').trim() || 'Участник';
}

/** Однострочное превью содержимого (без учёта группы / заголовка окна уведомления). */
export function buildMessengerMessagePreviewLine(msg: MessengerMessage): string {
  if (msg.deleted_at) return 'Сообщение удалено';

  if (msg.message_kind === 'system') {
    const t = (msg.body?.trim() ?? '').replace(/\r?\n/g, ' ').replace(/\s+/g, ' ').trim();
    return t || 'Событие в чате';
  }

  const body = (msg.body?.trim() ?? '').replace(/\r?\n/g, ' ').replace(/\s+/g, ' ').trim();

  const hasFile = Boolean(msg.file_url);
  const isImage = hasFile && isMessengerImageMime(msg.mime_type);
  const isFile = hasFile && !isImage;

  let inner: string;
  if (body) {
    inner = body;
  } else if (isImage) {
    inner = 'Изображение';
  } else if (isFile) {
    const fn = msg.file_name?.trim();
    inner = fn ? `Файл: ${fn}` : 'Файл';
  } else {
    const ctx = formatContextLine(normalizeContextAttachments(msg.context_attachments));
    inner = ctx || 'Новое сообщение';
  }

  const fwd = (msg.forwarded_from_sender_name ?? '').trim();
  if (fwd) {
    if (inner === 'Новое сообщение' && !body && !hasFile) {
      return `Переслано от ${fwd}`;
    }
    return `Переслано от ${fwd} · ${inner}`;
  }

  return inner;
}

/** Заголовок и текст для системного уведомления (как Telegram: в группе — название чата в title). */
export function buildMessengerNotificationContent(
  msg: MessengerMessage,
  threadMeta: MessengerSocketThreadMeta | null | undefined
): { title: string; body: string } {
  const preview = buildMessengerMessagePreviewLine(msg);
  const sender = senderShortName(msg);
  const isGroup = threadMeta?.thread_kind === 'group';

  if (isGroup) {
    const chatTitle = (threadMeta?.title ?? '').trim() || 'Группа';
    return { title: chatTitle, body: `${sender}: ${preview}` };
  }

  return { title: sender, body: preview };
}

/** Уведомление о пакете сообщений: заголовок как у последнего, в body — превью последнего и счётчик. */
export function buildMessengerBatchNotificationContent(
  messages: MessengerMessage[],
  threadMeta: MessengerSocketThreadMeta | null | undefined
): { title: string; body: string } {
  const last = messages[messages.length - 1]!;
  const { title, body: lastBody } = buildMessengerNotificationContent(last, threadMeta);
  if (messages.length <= 1) return { title, body: lastBody };
  const extra = messages.length - 1;
  return { title, body: `${lastBody} (+${extra})` };
}
